import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import {
  QUARTERLY_OKR,
  QUARTERLY_OKR_LIVE_REVIEW,
  QUARTERLY_OKR_REVIEW,
  QUARTERLY_OKR_SCORECARD,
} from "../../../../data/RouteUrls";
import SummaryGroups from "../../StrategyCanvas/SummaryPanel/SummaryPanel";
import { StandardTooltip } from "../../../shared/StandardTooltip/StandardTooltip";
import { readError } from "../_config/quarterlyOkrConfig";
import {
  briefRoute,
  quarterCrumbs,
  quarterRowOf,
} from "../_config/quarterlyOkrRoutes";
import quarterlyOkrStore, {
  useQuarterlyOkrStore,
} from "../_store/quarterlyOkrStore";
import liveReviewStore, {
  useQuarterlyOkrLiveReviewStore,
} from "./_socket/quarterlyOkrLiveReviewSocketStore";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EditIcon,
  StatusPill,
  TrashIcon,
} from "../../StrategyCanvas/StrategyCanvasHelpers";
import LivePresence from "./LivePresence/LivePresence";
import LiveReviewBody from "./LiveReviewBody/LiveReviewBody";
import LiveReviewSkeleton from "./LiveReviewSkeleton/LiveReviewSkeleton";
import QokrModeToggle from "../_shared/QokrModeToggle/QokrModeToggle";
import QokrAlignmentView, {
  alignmentFromLive,
} from "../_shared/QokrAlignmentView/QokrAlignmentView";
import "../../ExecutionBlueprint/ExecutionBlueprintLiveReview/ExecutionBlueprintLiveReview.scss";
import "./_quarterly-okr-live-review.scss";

// ═══════════════════════════════════════════════════════════════════════════
// `/coach-panel/quarterly-okrs/:quarterCompanyId/live-review` — the coach's
// seat in the live review (§10), in EXW's live review chrome: one header
// (title, who's in the room, to-discuss count), then the session body
// (objectives ‖ Meeting Agenda, Lock in the agenda footer).
//
// ⚠️ THE BODY IS THE CEO APP'S. `LiveReviewBody/`, `LiveObjectiveRow/`,
// `LivePresence/`, `LiveReviewSkeleton/`, the editor, the lock dialog and
// `_socket/` are the same code as `10x-frontend`'s
// `UserPanel/QuarterlyOkr/QuarterlyOkrLiveReview/`, at the same folder depth —
// the CEO and the coach sit in one room and must see one board. This file owns
// only the coach chrome and finding the run id from the quarter id.
// ═══════════════════════════════════════════════════════════════════════════

// EXW's own icons and status pill, so the rows match its live review.
// Review Mode | Alignment Mode (2026-10-28) — Strategy Canvas's Review /
// Canvas Mode and EXW's Review / Blueprint Mode, for Quarterly OKRs. Alignment
// Mode is the read-only "how OKRs link up" view (company objective → the team
// objectives linked to it), from the live snapshot.
const MODE = { REVIEW: "review", ALIGNMENT: "alignment" };
const MODE_OPTIONS = [
  { key: MODE.REVIEW, label: "Review Mode" },
  { key: MODE.ALIGNMENT, label: "Alignment Mode" },
];

const EXW_ICONS = {
  StatusPill,
  CheckIcon,
  EditIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
};

const selectDetails = (s) => s.details;
const selectRole = (s) => s.role;
const selectClients = (s) => s.clients;
const selectPresence = (s) => s.presence;
const selectPending = (s) => s.pending;
const selectLocked = (s) => s.locked;
const selectNotStarted = (s) => s.notStarted;
const selectDrafting = (s) => s.draftingUserIds;

const QuarterlyOkrLiveReview = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const HeaderMetaData = useContext(HeaderContext);
  const { quarterCompanyId: qcId } = useParams();

  const details = useQuarterlyOkrLiveReviewStore(selectDetails);
  const role = useQuarterlyOkrLiveReviewStore(selectRole);
  const presence = useQuarterlyOkrLiveReviewStore(selectPresence);
  const pending = useQuarterlyOkrLiveReviewStore(selectPending);
  const locked = useQuarterlyOkrLiveReviewStore(selectLocked);
  const notStarted = useQuarterlyOkrLiveReviewStore(selectNotStarted);
  const draftingUserIds = useQuarterlyOkrLiveReviewStore(selectDrafting);

  // The dashboard row says whether this quarter has a coach brief, which the
  // crumbs and Back then lead to (the Strategy Canvas pattern).
  const quarterRow = quarterRowOf(useQuarterlyOkrStore(selectClients), qcId);
  const [mode, setMode] = useState(MODE.REVIEW);
  const [runId, setRunId] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    document.title = "Art of Scale | Quarterly OKR Live Review";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Quarterly OKRs" });
    if (!quarterlyOkrStore.getState().clients) {
      quarterlyOkrStore
        .getState()
        .fetchDashboard()
        .catch(() => null);
    }
    // eslint-disable-next-line
  }, []);

  // The socket joins by run id; the coach's link carries the quarter id. The
  // review payload has both (and the company name for the crumb).
  useEffect(() => {
    quarterlyOkrStore
      .getState()
      .fetchReview(qcId, { force: true })
      .then((review) => {
        setRunId(review?.run_unique_id || null);
        setCompanyName(review?.company?.name || "");
      })
      .catch((error) => setLoadError(readError(error).message));
  }, [qcId]);

  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(
        QUARTERLY_OKR_LIVE_REVIEW,
        quarterRow?.brief_status
          ? quarterCrumbs({
              root: breadCrumbsArray[QUARTERLY_OKR][0],
              qcId,
              row: quarterRow,
              companyName,
              title: "Live Review",
            })
          : [
              breadCrumbsArray[QUARTERLY_OKR][0],
              {
                title: companyName
                  ? `${companyName} Live Review`
                  : "Live Review",
                url: "",
              },
            ],
      ),
    );
  }, [dispatch, companyName, quarterRow, qcId]);

  // Join once the run is known; leaving ends presence (hard close).
  useEffect(() => {
    if (!runId) return undefined;
    liveReviewStore.getState().connect(runId);
    return () => liveReviewStore.getState().disconnect();
  }, [runId]);

  useEffect(() => {
    if (notStarted) {
      history.replace(QUARTERLY_OKR_REVIEW.replace(":quarterCompanyId", qcId));
    }
  }, [notStarted, history, qcId]);

  // Locked: the result is the quarter's scorecard (it used to be the summary,
  // when the coach had no tracking read — see `QOKR_URLS.TRACKING`).
  useEffect(() => {
    if (!locked) return;
    quarterlyOkrStore
      .getState()
      .fetchDashboard()
      .catch(() => null);
    history.push(QUARTERLY_OKR_SCORECARD.replace(":quarterCompanyId", qcId));
  }, [locked, history, qcId]);

  return (
    <div>
      <ProjectHeader
        handleNavigation={() =>
          history.push(
            quarterRow?.brief_status ? briefRoute(qcId) : QUARTERLY_OKR,
          )
        }
        showBack
      />

      <div id="quarterly-okr-live-review">
        <div className="eblr-header">
          <div className="eblr-header-left">
            <div className="eblr-header-title">Live Review</div>
            {details ? (
              <LivePresence
                participants={presence}
                members={details.members || []}
                viewerRole={role}
              />
            ) : null}
          </div>
          {details ? (
            <div className="eblr-header-right">
              <div className="eblr-agenda-count">
                To discuss:<strong> {details.to_discuss_remaining || 0}</strong>
              </div>
              <QokrModeToggle
                options={MODE_OPTIONS}
                value={mode}
                onChange={setMode}
                ariaLabel="Live review view"
              />
            </div>
          ) : null}
        </div>
        <div className="eblr-header-divider" />

        {loadError ? (
          <div className="aokr-empty">{loadError}</div>
        ) : details && mode === MODE.ALIGNMENT ? (
          <div className="qokr-lr-align qokr-mode-pane" key="alignment">
            <QokrAlignmentView teamOkrs={alignmentFromLive(details)} />
          </div>
        ) : details ? (
          <LiveReviewBody
            details={details}
            role={role}
            pending={pending}
            draftingUserIds={draftingUserIds}
            summaryGroupsComponent={SummaryGroups}
            tooltipComponent={StandardTooltip}
            icons={EXW_ICONS}
          />
        ) : (
          <LiveReviewSkeleton />
        )}
      </div>
    </div>
  );
};

export default QuarterlyOkrLiveReview;
